import { useCallback } from 'react';
import { Cache } from 'three';
import { useAdminSubjectDraft } from '@/contexts';

type FbxField = '_modelBlobUrl' | '_contextBlobUrl' | '_referenceContextBlobUrl';

/**
 * File upload handlers for draft FBX models and textures.
 *
 * Each handler creates a blob URL, pre-populates THREE.Cache with the file
 * contents (so Three.js loaders never call fetch() on blob:// URLs, which
 * would violate CSP connect-src), updates draft config, and persists the
 * file to IndexedDB for page-reload recovery.
 */
export function useDraftFileUpload() {
  const { updateConfig, persistDraftFile } = useAdminSubjectDraft();

  const uploadFbx = useCallback(
    async (field: FbxField, file: File) => {
      const blobUrl = URL.createObjectURL(file);
      Cache.add(blobUrl, await file.arrayBuffer());
      if (field === '_modelBlobUrl') {
        // Changing the subject model invalidates the existing texture.
        updateConfig({ _modelBlobUrl: blobUrl, _textureBlobUrl: undefined, texturePath: '' });
      } else {
        updateConfig({ [field]: blobUrl });
      }
      persistDraftFile(field, file);
    },
    [updateConfig, persistDraftFile]
  );

  const uploadTexture = useCallback(
    async (file: File) => {
      const blobUrl = URL.createObjectURL(file);
      Cache.add(blobUrl, await createImageBitmap(file));
      updateConfig({ _textureBlobUrl: blobUrl });
      persistDraftFile('_textureBlobUrl', file);
    },
    [updateConfig, persistDraftFile]
  );

  return { uploadFbx, uploadTexture };
}
